import * as BenchlingUtil from 'BenchlingUtil';
import {EventConstants} from 'constants/EventConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {GridSchemaClass} from 'constants/SampleConstants';
import AppDispatcher from 'lib/Dispatcher';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import {ClientSchemaField} from 'registry/field/FieldTable';
import RegistryActions from 'registry/RegistryActions';
import * as RegistryUtil from 'registry/RegistryUtil';
import {
  BoxSchema,
  ContainerSchema,
  LocationSchema,
  PlateSchema,
} from 'samples/storables/StorableSchemaConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import APIUtil from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';

import * as BoxSchemaDAO from './BoxSchemaDAO';
import * as ContainerSchemaDAO from './ContainerSchemaDAO';
import * as LocationSchemaDAO from './LocationSchemaDAO';
import * as PlateSchemaDAO from './PlateSchemaDAO';

export function resetSavingState(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BATCH_SCHEMA_SAVING_COMPLETED,
  });
}

export function fetchAllBoxSchemas(): void {
  BoxSchemaDAO.fetchAll()
    .done(({data}: {data: Array<BoxSchema>}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.BOX_SCHEMAS_FETCH_COMPLETED,
        boxSchemas: data,
      });
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((error: any) => {
      notifications.notifyError(error);
    });
}

/**
 * When a schema is created, notify the user of the successful creation and show an action button to directly
 * open it.
 *
 * This is needed for the user because we navigate back to the schema listing page after creating a schema, so
 * this gives them a way to quickly navigate to the schema they just created. This applies to box schema,
 * plate schema, and location schema, but not container schema (because there is no standalone container
 * schema settings page).
 */
function notifyCreateSuccess(storableSchemaId: string, registryId: string): void {
  const typeName = typeToName(APIUtil.apiIdToType(storableSchemaId));
  notifications.success(`Created ${typeName} successfully`, {
    actionText: 'Open',
    duration: 10,
    onActionClick: async () => {
      // Currently, we have to construct the schema URL by using the `registryId` and fetching the
      // `ownerUrlId`. Ideally, the server response would have the URL (`navigateToURL(response.url)`), or the
      // schema URL would simply only require the `storableSchemaId`
      // (`navigateToURL(RegistryUtil.getStorableSchemaSettingsUrl(storableSchemaId))`).
      const ownerUrlId = await RegistryActions.getOwnerUrlId(registryId, {
        loadingMessage: `Opening ${typeName}...`,
      });
      const url = RegistryUtil.getStorableSchemaSettingsUrl(ownerUrlId, registryId, storableSchemaId);
      navigateToURL(url);
    },
  });
}

export function createBoxSchema(
  registryId: string,
  createParams: CreateBoxSchemaParams,
  onSuccess?: () => void
): void {
  LoadingScreen.show('Saving...');
  BoxSchemaDAO.create({registryId, ...createParams})
    .done((boxSchema: BoxSchema) => {
      if (onSuccess) {
        onSuccess();
      }
      AppDispatcher.dispatch({
        actionType: EventConstants.BOX_SCHEMA_CREATE_COMPLETED,
        boxSchema,
      });
      notifyCreateSuccess(boxSchema.id, registryId);
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((error: any) => {
      notifications.notifyError(error);
    })
    .always(() => {
      LoadingScreen.hide();
    });
}

export function deleteBoxSchema(boxSchemaId: string): void {
  BoxSchemaDAO.deleteBoxSchema(boxSchemaId)
    .done(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.BOX_SCHEMA_DELETE_COMPLETED,
        boxSchemaId,
      });
      notifications.success('Box schema deleted.');
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((error: any) => {
      notifications.notifyError(error);
    });
}

export function updateBoxSchema(boxSchemaId: string, updateParams: UpdateBoxSchemaParams): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BOX_SCHEMA_UPDATE,
  });
  BoxSchemaDAO.update(boxSchemaId, updateParams)
    .done((boxSchema: BoxSchema) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.BOX_SCHEMA_UPDATE_COMPLETED,
        boxSchema,
      });
      notifications.success('Updated box schema successfully.');
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((response: any) => {
      notifications.notifyError(response);
      AppDispatcher.dispatch({
        actionType: EventConstants.BOX_SCHEMA_UPDATE_FAILED,
      });
    });
}

export function fetchAllPlateSchemas(): void {
  PlateSchemaDAO.fetchAll()
    .done(({data}: {data: Array<PlateSchema>}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.PLATE_SCHEMAS_FETCH_COMPLETED,
        plateSchemas: data,
      });
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((error: any) => {
      notifications.notifyError(error);
    });
}

export function createPlateSchema(
  registryId: string,
  createParams: CreatePlateSchemaParams,
  onSuccess?: () => void
): void {
  LoadingScreen.show('Saving...');
  PlateSchemaDAO.create({registryId, ...createParams})
    .done((plateSchema: PlateSchema) => {
      if (onSuccess) {
        onSuccess();
      }
      AppDispatcher.dispatch({
        actionType: EventConstants.PLATE_SCHEMA_CREATE_COMPLETED,
        plateSchema,
      });
      notifyCreateSuccess(plateSchema.id, registryId);
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((error: any) => {
      notifications.notifyError(error);
    })
    .always(() => {
      LoadingScreen.hide();
    });
}

export function deletePlateSchema(plateSchemaId: string): void {
  PlateSchemaDAO.deletePlateSchema(plateSchemaId)
    .done(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.PLATE_SCHEMA_DELETE_COMPLETED,
        plateSchemaId,
      });
      notifications.success('Plate schema deleted.');
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((error: any) => {
      notifications.notifyError(error);
    });
}

export function updatePlateSchema(plateSchemaId: string, updateParams: UpdatePlateSchemaParams): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.PLATE_SCHEMA_UPDATE,
  });
  PlateSchemaDAO.update(plateSchemaId, updateParams)
    .done((plateSchema: PlateSchema) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.PLATE_SCHEMA_UPDATE_COMPLETED,
        plateSchema,
      });
      notifications.success('Updated plate schema successfully.');
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((response: any) => {
      notifications.notifyError(response);
      AppDispatcher.dispatch({
        actionType: EventConstants.PLATE_SCHEMA_UPDATE_FAILED,
      });
    });
}

// TODO(T10317): Remove grid schema actions
export function fetchAllGridSchemas(gridSchemaClass: GridSchemaClass): void {
  switch (gridSchemaClass as unknown as ModelTypeConstants) {
    case ModelTypeConstants.BOX_SCHEMA:
      fetchAllBoxSchemas();
      break;
    case ModelTypeConstants.PLATE_SCHEMA:
      fetchAllPlateSchemas();
      break;
    default:
      break;
  }
}

// TODO(T10317): Remove grid schema actions
export function createGridSchema(
  registryId: string,
  data: CreateGridSchemaParams,
  gridSchemaClass: GridSchemaClass,
  onSuccess: () => void
): void {
  switch (gridSchemaClass as unknown as ModelTypeConstants) {
    case ModelTypeConstants.BOX_SCHEMA:
      createBoxSchema(registryId, data, onSuccess);
      break;
    case ModelTypeConstants.PLATE_SCHEMA:
      createPlateSchema(registryId, data, onSuccess);
      break;
    default:
      break;
  }
}

// TODO(T10317): Remove grid schema actions
export function deleteGridSchema(gridSchemaId: string, gridSchemaClass: GridSchemaClass): void {
  switch (gridSchemaClass as unknown as ModelTypeConstants) {
    case ModelTypeConstants.BOX_SCHEMA:
      deleteBoxSchema(gridSchemaId);
      break;
    case ModelTypeConstants.PLATE_SCHEMA:
      deletePlateSchema(gridSchemaId);
      break;
    default:
      break;
  }
}

// TODO(T10317): Remove grid schema actions
export function updateGridSchema(
  gridSchemaId: string,
  data: UpdateGridSchemaParams,
  gridSchemaClass: GridSchemaClass
): void {
  switch (gridSchemaClass as unknown as ModelTypeConstants) {
    case ModelTypeConstants.BOX_SCHEMA:
      updateBoxSchema(gridSchemaId, data);
      break;
    case ModelTypeConstants.PLATE_SCHEMA:
      updatePlateSchema(gridSchemaId, data);
      break;
    default:
      break;
  }
}

export function fetchAllContainerSchemas(): void {
  ContainerSchemaDAO.fetchAll()
    .done(({data}: {data: Array<ContainerSchema>}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.CONTAINER_SCHEMAS_FETCH_COMPLETED,
        containerSchemas: data,
      });
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((error: any) => {
      notifications.notifyError(error);
    });
}

export function createContainerSchema(registryId: string, createParams: CreateContainerSchemaParams): void {
  ContainerSchemaDAO.create({registryId, ...createParams})
    .done((containerSchema: ContainerSchema) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.CONTAINER_SCHEMA_CREATE_COMPLETED,
        containerSchema,
      });
      notifications.success('Created container schema successfully');
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((error: any) => {
      notifications.notifyError(error);
    });
}

export function updateContainerSchema(
  containerSchemaId: string,
  updateParams: UpdateContainerSchemaParams
): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.CONTAINER_SCHEMA_UPDATE,
  });
  ContainerSchemaDAO.update(containerSchemaId, updateParams)
    .done((containerSchema: ContainerSchema) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.CONTAINER_SCHEMA_UPDATE_COMPLETED,
        containerSchema,
      });
      notifications.success('Updated container schema successfully.');
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((response: any) => {
      notifications.notifyError(response);
      AppDispatcher.dispatch({
        actionType: EventConstants.CONTAINER_SCHEMA_UPDATE_FAILED,
      });
    });
}

export function deleteContainerSchema(containerSchemaId: string): void {
  ContainerSchemaDAO.deleteContainerSchema(containerSchemaId)
    .done(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.CONTAINER_SCHEMA_DELETE_COMPLETED,
        containerSchemaId,
      });
      notifications.success('Container schema deleted.');
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((error: any) => {
      notifications.notifyError(error);
    });
}

export function fetchAllLocationSchemas(): void {
  LocationSchemaDAO.fetchAll()
    .done(({data}: {data: Array<LocationSchema>}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.LOCATION_SCHEMAS_FETCH_COMPLETED,
        locationSchemas: data,
      });
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((error: any) => {
      notifications.notifyError(error);
    });
}

export function createLocationSchema(
  registryId: string,
  ownerUrlId: string,
  createParams: CreateLocationSchemaParams
): void {
  LoadingScreen.show('Saving...');
  LocationSchemaDAO.create({registryId, ...createParams})
    .done((locationSchema: LocationSchema) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.LOCATION_SCHEMA_CREATE_COMPLETED,
        locationSchema,
      });
      BenchlingUtil.navigate(null, RegistryUtil.getRegistryLocationSchemasUrl(ownerUrlId, registryId));
      notifyCreateSuccess(locationSchema.id, registryId);
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((error: any) => {
      notifications.notifyError(error);
    })
    .always(() => {
      LoadingScreen.hide();
    });
}

export function updateLocationSchema(
  locationSchemaId: string,
  updateParams: UpdateLocationSchemaParams
): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.LOCATION_SCHEMA_UPDATE,
  });
  LocationSchemaDAO.update(locationSchemaId, updateParams)
    .done((locationSchema: LocationSchema) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.LOCATION_SCHEMA_UPDATE_COMPLETED,
        locationSchema,
      });
      notifications.success('Updated location schema successfully.');
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((response: any) => {
      notifications.notifyError(response);
      AppDispatcher.dispatch({
        actionType: EventConstants.LOCATION_SCHEMA_UPDATE_FAILED,
      });
    });
}

export function deleteLocationSchema(locationSchemaId: string): void {
  LocationSchemaDAO.deleteLocationSchema(locationSchemaId)
    .done(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.LOCATION_SCHEMA_DELETE_COMPLETED,
        locationSchemaId,
      });
      notifications.success('Location schema deleted.');
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .fail((error: any) => {
      notifications.notifyError(error);
    });
}

export interface CreateContainerSchemaParams {
  allFields?: Array<SchemaField | ClientSchemaField>;
  name: string;
  prefix: string;
  schemaDescription?: string;
}

export interface UpdateContainerSchemaParams extends CreateContainerSchemaParams {
  sqlIdentifier: string;
}

export interface CreateBoxSchemaParams extends CreateContainerSchemaParams {
  height: number;
  width: number;
  containerSchemaId: string | null;
}

export interface UpdateBoxSchemaParams extends CreateBoxSchemaParams {
  sqlIdentifier: string;
}

export interface CreatePlateSchemaParams extends CreateBoxSchemaParams {
  plateSchemaType?: string;
}

export interface UpdatePlateSchemaParams extends CreatePlateSchemaParams {
  sqlIdentifier: string;
}

export type CreateGridSchemaParams = CreateBoxSchemaParams | CreatePlateSchemaParams;
export type UpdateGridSchemaParams = UpdateBoxSchemaParams | UpdatePlateSchemaParams;

export type CreateLocationSchemaParams = CreateContainerSchemaParams;
export type UpdateLocationSchemaParams = UpdateContainerSchemaParams;
